<script lang="ts">
  import { onDestroy } from "svelte";
  import { form, style, field } from "svelte-forms2";
  import { required } from "svelte-forms2/validators";
  import type { FieldValidation } from "svelte-forms2";
  import { createEventDispatcher } from "svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import type { Errors } from "@shared/types/Errors";
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import ValidationError from "@shared/svelte/Components/ValidationError.svelte";
  import SalesRepStore from "../Stores/SalesRepStore";
  import type { SalesRepToAdd } from "@shared/types/SalesRep";
  import type { Distributor } from "@shared/types/Distributor";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import * as SalesRepDomainValidationService from "@shared/services/sales_rep_domain_validation";
  import { emailOrEmpty } from "@shared/svelte/Validators/Email";
  import { markDirty } from "@shared/svelte/Validators/utils";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { ErrorMessageEntity } from "@shared/services/event_tracking/entities";

  export let distributor: Distributor;
  export let addingRep: boolean;
  export let addSalesRepErrorCallback;

  const { trackClick, trackEvent } = EventTracking;

  const contactInfoError = "Contact information is required";
  const createSalesRepStore = SalesRepStore({ initial: {}, distributorId: distributor.id });
  const cancelAdd = () => (addingRep = false);
  const dispatch = createEventDispatcher();

  let errors: Errors = { messages: [] };
  let submitting: boolean = false;
  let showContactInfoError: boolean = false;

  function distributorDomainValidator(value: any): FieldValidation {
    if (value === "") return { valid: true, name: "invalidDomain" };
    if (!value.split("@")[1]) return { valid: true, name: "invalidDomain" };

    let domain = value.split("@")[1];

    return {
      valid: SalesRepDomainValidationService.isValidDomain(domain, distributor.id) || value == "",
      name: "invalidDomain",
    };
  }

  const nameField = field("name", "", [required()], { valid: false });
  const contactEmailField = field("contact_email", "", [emailOrEmpty, distributorDomainValidator], {
    validateOnChange: false,
  });
  const contactPhoneField = field("contact_phone", "");

  const fields = [nameField, contactEmailField, contactPhoneField];

  const addSalesRepForm = form(...fields);

  const errorMessages = {
    invalidDomain: value =>
      "This email belongs to a different distributor. Select a different distributor or enter a new email.",
  };

  const runSubmitUserWrapper = async () => {
    let repToAdd: SalesRepToAdd = {
      name: $nameField.value,
      contact_email: $contactEmailField.value,
      contact_phone: $contactPhoneField.value,
    };

    if ((showContactInfoError = repToAdd.contact_phone == "" && repToAdd.contact_email == "")) {
      trackEvent("begin_to_render", [new ErrorMessageEntity({ messages: [contactInfoError] })]);
    }

    await addSalesRepForm.validate();

    // form may be valid but still if contact info is incomplete, it should not save
    if (!$addSalesRepForm.valid || showContactInfoError) {
      fields.forEach(field => markDirty(field));

      return;
    }

    submitting = true;
    createSalesRepStore
      .save(repToAdd)
      .then(addedRep => {
        addingRep = false;
        dispatch("repAdded", addedRep);
      })
      .catch(async (error: any) => {
        submitting = false;
        errors = parseApiErrorResponse(error);
        trackEvent("begin_to_render", [new ErrorMessageEntity({ messages: errors.messages })]);
        addSalesRepErrorCallback(error);
      });
  };

  const parseApiErrorResponse = (response: any): Errors => {
    if (!response) return;
    if (!Object.entries(response).length) return;

    if (response.errors) return response.errors;
    if (response.error) {
      return {
        messages: errors?.messages ? [...errors.messages, response.error] : [response.error],
      };
    }
    if (Array.isArray(response)) return { messages: response };
  };

  const unsubscribeErrors = createSalesRepStore.errors.subscribe(messages => {
    return (errors = messages);
  });

  onDestroy(unsubscribeErrors);
</script>

<FormErrors {errors} />
<!-- svelte-ignore component-name-lowercase -->
<form on:submit|preventDefault={runSubmitUserWrapper}>
  <div class="fizz-bordered fizz-inset-square-16">
    <div class="fizz-stack-16">
      <label for="name" class="fizz-label fizz-stack-4">Full Name</label>
      <input
        type="text"
        id="name"
        name="name"
        class="fizz-input-text fizz-full-width"
        bind:value={$nameField.value}
        use:style={{ field: nameField }}
        on:click={() => trackClick("onboarding_sales_rep_name_field")}
      />
      <ValidationError name="Name" fieldObj={$nameField} />
    </div>

    <div class="fizz-flex-row fizz-stack-24 fizz-align-content-start">
      <div class="fizz-flex-item">
        <label for="contact_email" class="fizz-label fizz-stack-4">Email</label>
        <input
          type="email"
          id="contact_email"
          name="contact_email"
          class="fizz-input-text fizz-full-width stack-8"
          bind:value={$contactEmailField.value}
          use:style={{ field: contactEmailField }}
          on:click={() => trackClick("onboarding_sales_rep_email_field")}
        />
        <ValidationError {errorMessages} name="Email" fieldObj={$contactEmailField} />
      </div>

      <span style="padding-top: 24px" class="fizz-text-medium fizz-stack-4">or</span>

      <div class="fizz-flex-item">
        <label for="contact_phone" class="fizz-label fizz-stack-4">Phone</label>
        <PhoneInput
          extraClasses="fizz-input-text fizz-full-width"
          bind:value={$contactPhoneField.value}
          placeholder=""
          onFocus={() => trackClick("onboarding_sales_rep_phone_field")}
        />
      </div>
    </div>
    {#if showContactInfoError}
      <p class="fizz-flex-row-4 fizz-text-urgent fizz-text-medium">
        <Icon type="Info" />
        <span>{contactInfoError}</span>
      </p>
    {/if}
    <div class="fizz-flex-row-8 fizz-justify-end">
      <button on:click={cancelAdd} class="fizz-button fizz-button-small">Cancel</button>
      <button class="fizz-button-secondary fizz-button-small" disabled={submitting}>Save</button>
    </div>
  </div>
</form>
